"use client";

import { Button } from "@heroui/react";
import { Icon } from "@iconify/react";
import { useTranslations } from "next-intl";
import { useMemo } from "react";
import { LuMenu } from "react-icons/lu";

import { UnifiedChatBell } from "@/components/chat/UnifiedChatBell";
import { useAuth } from "@/contexts/AuthContext";
import { useUserTeams } from "@/hooks/useUserTeams";
import { useUserTournaments } from "@/hooks/useUserTournaments";
import { Link } from "@/i18n/routing";
import { isUserAdmin } from "@/lib/users";

import type { TeamNavItem, TournamentNavItem } from "../PublicHeader";
import { TowerChallengerLogo } from "../TowerChallengerLogo";
import { UserMenu } from "../UserMenu";

type Props = {
  /**
   * When rendered on a tournament page, pass the active slug so the tower
   * shortcut links to it. Otherwise the icon links to the user's first
   * tournament (joined team first, then created).
   */
  tournamentSlug?: string;
  onOpenDrawer: () => void;
};

/**
 * Mobile header row: hamburger + logo on the left, user actions on the
 * right. Shared between non-tournament pages (PublicHeader) and tournament
 * pages (MobileNav), so the chrome stays consistent across the app.
 */
export function MobileHeaderBar({ tournamentSlug, onOpenDrawer }: Props) {
  const t = useTranslations();
  const { user, loading: userLoading } = useAuth();

  const { data: userTournaments = [] } = useUserTournaments(
    user?.tournamentIds,
  );
  const { data: userTeams = [] } = useUserTeams();

  const tournamentNavItems = useMemo<TournamentNavItem[]>(
    () =>
      userTournaments.map((tournament) => ({
        [tournament.id]: { name: tournament.name, slug: tournament.slug },
      })),
    [userTournaments],
  );
  const teamNavItems = useMemo<TeamNavItem[]>(
    () =>
      userTeams.map((team) => ({
        teamId: team.teamId,
        teamName: team.teamName,
        tournamentSlug: team.tournamentSlug,
        tournamentName: team.tournamentName,
      })),
    [userTeams],
  );

  const fallbackTournamentSlug = useMemo(() => {
    if (teamNavItems.length > 0) return teamNavItems[0].tournamentSlug;
    const first = tournamentNavItems[0];
    if (!first) return null;
    const id = Object.keys(first)[0];
    return id ? first[id]?.slug ?? null : null;
  }, [teamNavItems, tournamentNavItems]);

  const towerSlug = tournamentSlug ?? fallbackTournamentSlug;

  return (
    <div className="flex h-13 items-center justify-between gap-2 px-3">
      <div className="flex min-w-0 items-center gap-2">
        <Button
          isIconOnly
          variant="bordered"
          radius="md"
          size="sm"
          aria-label={t("nav.aria.openMainMenu")}
          onPress={onOpenDrawer}
          className="shrink-0 border-tn-border bg-transparent text-tn-text-secondary"
        >
          <LuMenu size={18} />
        </Button>

        <Link
          href="/"
          className="flex min-w-0 items-center"
          aria-label={t("brand.name")}
        >
          <TowerChallengerLogo size="sm" variant="full" />
        </Link>
      </div>

      <div className="flex shrink-0 items-center gap-1">
        {userLoading ? (
          <div
            role="status"
            aria-label={t("common.loading")}
            className="flex items-center gap-1"
          >
            <div className="size-9 rounded-md border border-tn-border bg-tn-elevated/50 animate-pulse" />
            <div className="size-9 rounded-md border border-tn-border bg-tn-elevated/50 animate-pulse" />
            <div className="h-9 w-9 rounded-md border border-tn-border bg-tn-elevated/50 animate-pulse" />
          </div>
        ) : user ? (
          <>
            {towerSlug && (
              <Link
                href={`/tournaments/${towerSlug}`}
                aria-label={t("header.nav.myHome")}
                className="flex size-9 items-center justify-center rounded-md border border-tn-border bg-transparent text-tn-text-secondary transition-colors hover:border-tn-gold/50 hover:bg-tn-elevated hover:text-tn-gold"
              >
                <Icon icon="game-icons:stone-tower" className="text-xl" />
              </Link>
            )}
            <UnifiedChatBell />
            <UserMenu
              className="ml-0"
              userName={user.name}
              tournamentNavItems={tournamentNavItems}
              teamNavItems={teamNavItems}
              isOrganizer={isUserAdmin(user)}
            />
          </>
        ) : (
          <Link
            href="/login"
            className="rounded-md border border-tn-gold/40 bg-tn-gold/10 px-3 py-1.5 font-display text-[12px] font-semibold uppercase tracking-[0.12em] text-tn-gold transition-colors hover:bg-tn-gold/15"
          >
            {t("auth.login.signIn")}
          </Link>
        )}
      </div>
    </div>
  );
}
